import {
  Box,
  Button,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
} from '@chakra-ui/react'
import { Trans } from '@lingui/react/macro'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { colors } from '../theme/cssVariables'
import { formatStr } from '../utils/format'
import { ErrorIcon, EventDropIcon, ShieldCheckIcon } from '../components/Icons/SkinIcons'
import { SkinWithFetchStatus } from '../types'
import { get } from '../api'
import { BaseURLS } from '../api/axios'
import { useLanguagePath } from '../hooks/useI18nRoute'

interface SkinTooltipProps {
  skin: SkinWithFetchStatus
  onCheck?: (skin: SkinWithFetchStatus) => void
  shieldCheckIconColor?: string
}

export const SkinTooltip = ({ skin, onCheck, shieldCheckIconColor }: SkinTooltipProps) => {
  return (
    <Popover trigger="hover" placement="top-end" closeOnBlur={true} isLazy>
      <PopoverTrigger>
        <Box cursor={'help'}>
          <ShieldCheckIcon color={shieldCheckIconColor} />
        </Box>
      </PopoverTrigger>

      <PopoverContent
        width="auto"
        bg={colors.colorB5}
        border={'none'}
        p={4}
        shadow={'none'}
        fontSize={['xs', 'sm']}
        outline={'none'}
        boxShadow={'none'}
        _focus={{
          outline: 'none',
          boxShadow: 'none',
        }}
      >
        <PopoverArrow bg={colors.colorB5} shadowColor={colors.colorB5} />
        <Flex alignItems={'center'} gap={2} px={1}>
          <Text color={colors.colorB3}>
            <Trans>Roll Id</Trans>:
          </Text>
          <Text color={colors.colorB1}>{formatStr(skin.instanceId, 5, 5)}</Text>
          <Button
            variant={'yellowOutline'}
            size={'sm'}
            onClick={(e) => {
              e.stopPropagation()
              onCheck?.(skin)
            }}
            bg={colors.colorA1}
            color={colors.colorB7}
            _hover={{
              bg: colors.colorA1,
              color: colors.colorB7,
            }}
          >
            <Trans>Check</Trans>
          </Button>
        </Flex>
      </PopoverContent>
    </Popover>
  )
}

export const SkinEventDropTooltip = ({ instanceId }: { instanceId: string }) => {
  const [eventInfo, setEventInfo] = useState<{
    rollDate: string
    roomId: string
    roomName: string
  } | null>(null)
  const getPath = useLanguagePath()

  const getEventInfo = async () => {
    if (instanceId) {
      try {
        const res = await get(
          `/api/events/skin/${instanceId}/room-info`,
          {},
          { baseURL: BaseURLS.event },
        )
        setEventInfo(res.roomInfo)
      } catch (error) {
        console.error('Error fetching event drop info:', error)
      }
    }
  }

  const handleOpen = () => {
    getEventInfo()
  }

  return (
    <Popover trigger="hover" placement="top-end" closeOnBlur={true} isLazy onOpen={handleOpen}>
      <PopoverTrigger>
        <Box cursor={'help'}>
          <EventDropIcon />
        </Box>
      </PopoverTrigger>
      <PopoverContent
        maxW={'300px'}
        bg={colors.colorB5}
        border={'none'}
        p={4}
        shadow={'none'}
        fontSize={['xs', 'sm']}
        outline={'none'}
        boxShadow={'none'}
        _focus={{
          outline: 'none',
          boxShadow: 'none',
        }}
      >
        <PopoverArrow bg={colors.colorB5} shadowColor={colors.colorB5} />
        <Box>
          <Text color={colors.colorB3}>
            <Trans>This skin is dropped from the event</Trans>:
          </Text>
          {!eventInfo ? (
            <Text color={colors.colorB1}>
              <Trans>Loading...</Trans>
            </Text>
          ) : (
            <Link to={getPath(`/event/room/${eventInfo?.roomId}`)}>
              <Text color={colors.colorA1} fontWeight={'bold'}>
                {eventInfo.roomName}
              </Text>
            </Link>
          )}
        </Box>
      </PopoverContent>
    </Popover>
  )
}
